import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import {
  Checkbox, Divider, FormControlLabel, IconButton, ListSubheader,
  makeStyles, Menu, MenuItem
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SydSelect from '../../commonDesign/SydSelect'
import SydButton from '../../commonDesign/Button'
import { ICON_NAMES } from '../../../constants'
import { useSaveExplorerConfiguration } from '../../../api/coreData'
import { QUICK_DATE_OPTIONS, COLUMN_CATALOG_MAP } from './lib/constants'
import CustomGroupingDialog from './CustomGroupingDialog'
import ColumnBuilderDialog from './columnBuilder'
import ClientContextBadge from './ClientContextBadge'
import SaveViewDialog from './SaveViewDialog'
import OpenViewDialog from './OpenViewDialog'

const GLOBAL_DATE_OPTIONS = [
  ...QUICK_DATE_OPTIONS,
  { value: '__custom__', label: 'Custom Range...' }
]

const useStyles = makeStyles(() => ({
  toolbarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid var(--opal-border-default, #e0e0e0)',
    background: 'var(--opal-surface-default, #fff)',
    flexShrink: 0
  },
  configBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderBottom: '1px solid var(--opal-border-default, #e0e0e0)',
    background: 'var(--theme-color-surface-d1, #f5f5f5)',
    fontSize: 12
  },
  clientBadge: {
    marginRight: 8,
    paddingRight: 12,
    borderRight: '1px solid var(--opal-border-default, #e0e0e0)'
  },
  viewsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  viewsBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--color-neutral-700, #555)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'var(--theme-color-surface-d2, #e8e8e8)'
    }
  },
  viewName: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--color-neutral-800, #333)',
    maxWidth: 240,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  viewNameMuted: {
    fontSize: 13,
    fontStyle: 'italic',
    color: 'var(--color-neutral-400, #aaa)'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    flexShrink: 0
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 200,
    flex: 1,
    maxWidth: 360
  },
  fieldNarrow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 120,
    maxWidth: 200,
    flex: '0 0 auto'
  },
  label: {
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)'
  },
  summaryBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 8px',
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    fontSize: 13,
    color: 'var(--color-neutral-700, #555)',
    minHeight: 32,
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    '&:hover': {
      borderColor: 'var(--color-primary-500, #1976d2)'
    }
  },
  summaryText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  summaryPlaceholder: {
    color: 'var(--color-neutral-400, #aaa)'
  },
  summaryCount: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    flexShrink: 0
  },
  dateRangeRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center'
  },
  dateInput: {
    flex: 1,
    padding: '6px 8px',
    fontSize: 13,
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'inherit',
    outline: 'none',
    minWidth: 0,
    '&:focus': {
      outline: '1px solid var(--color-primary-500, #1976d2)'
    }
  },
  closeBtn: {
    padding: 4,
    flexShrink: 0
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto'
  },
  toggleLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      color: 'var(--color-neutral-600, #666)'
    }
  },
  recentHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    lineHeight: '28px',
    color: 'var(--color-neutral-500, #888)'
  },
  recentActive: {
    fontWeight: 600,
    color: 'var(--color-primary-600, #1565c0)'
  },
  menuItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 16
  },
  kbdHint: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    fontFamily: 'var(--font-family-monospace, ui-monospace, SFMono-Regular, monospace)'
  }
}))

function detectShortcutLabel () {
  if (typeof navigator === 'undefined') return 'Ctrl+Shift+.'
  const platform = (navigator.userAgentData?.platform || navigator.platform || '').toLowerCase()
  return /mac|iphone|ipad|ipod/.test(platform) ? '⌘⇧.' : 'Ctrl+Shift+.'
}

function RecentViewsSection ({ savedConfigs, recentViews, activeConfigId, onLoadConfig, onClose, classes }) {
  const configsById = new Map((savedConfigs || []).map(c => [c.explorerConfigurationId, c]))
  const validRecents = (recentViews || []).filter(r => configsById.has(r.id))
  if (validRecents.length === 0) return null
  return (
    <>
      <Divider />
      <ListSubheader className={classes.recentHeader} disableSticky>Recent</ListSubheader>
      {validRecents.map(r => (
        <MenuItem
          key={r.id}
          className={r.id === activeConfigId ? classes.recentActive : undefined}
          onClick={() => {
            onClose()
            onLoadConfig(configsById.get(r.id))
          }}
        >
          {r.name}
        </MenuItem>
      ))}
    </>
  )
}

RecentViewsSection.propTypes = {
  savedConfigs: PropTypes.arrayOf(PropTypes.object),
  recentViews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  })),
  activeConfigId: PropTypes.number,
  onLoadConfig: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

function getColumnSummary (columns) {
  if (!columns || columns.length === 0) return null
  return columns.map(col => {
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    const label = col.label ?? catalog?.label ?? col.columnType
    const opts = col.options
    if (opts?.dateMode === 'quickDate' && opts?.quickDate) return `${label} (${opts.quickDate})`
    if (opts?.dateMode === 'dateRange' && opts?.startDate && opts?.endDate) {
      return `${label} (${dayjs(opts.startDate).format('M/D')}-${dayjs(opts.endDate).format('M/D')})`
    }
    return label
  }).join(', ')
}

export default function ExplorerConfigToolbar ({
  config,
  setGroupings,
  setColumns,
  setQuickDate,
  setDateRange,
  setFetchAllLevels,
  setIncludeBenchmarks,
  isFetching,
  onRefresh,
  canRun,
  savedConfigs,
  activeConfigId,
  activeViewName,
  isDirty,
  onLoadConfig,
  onDeleteConfig,
  onRefreshConfigs,
  serializeConfig,
  onSaveComplete,
  recentViews,
  asOfDate,
  setAsOfDate,
  onOpenPrefs
}) {
  const classes = useStyles()
  const [groupingDialogOpen, setGroupingDialogOpen] = useState(false)
  const [columnDialogOpen, setColumnDialogOpen] = useState(false)
  const [viewsMenuAnchor, setViewsMenuAnchor] = useState(null)
  const [openViewDialogOpen, setOpenViewDialogOpen] = useState(false)
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false)
  const saveMutation = useSaveExplorerConfiguration()

  const isCustomDateRange = config.dateMode === 'dateRange'

  const handleViewsClick = useCallback((e) => {
    setViewsMenuAnchor(e.currentTarget)
  }, [])

  const handleViewsClose = useCallback(() => {
    setViewsMenuAnchor(null)
  }, [])

  const handleOpenViewDialog = useCallback(() => {
    setViewsMenuAnchor(null)
    setOpenViewDialogOpen(true)
    if (onRefreshConfigs) onRefreshConfigs()
  }, [onRefreshConfigs])

  const handleSaveViewDialog = useCallback(() => {
    setViewsMenuAnchor(null)
    setSaveViewDialogOpen(true)
  }, [])

  const handleSaveInPlace = useCallback(() => {
    setViewsMenuAnchor(null)
    if (!activeConfigId || saveMutation.isLoading) return
    saveMutation.mutate({
      explorerConfigurationId: activeConfigId,
      name: activeViewName,
      ...serializeConfig()
    }, {
      onSuccess: (data) => {
        onSaveComplete({ explorerConfigurationId: data.explorerConfigurationId, name: activeViewName })
      }
    })
  }, [activeConfigId, activeViewName, saveMutation, serializeConfig, onSaveComplete])

  const handleGroupingApply = useCallback(({ groupings, includeBenchmarks }) => {
    setGroupings(groupings)
    setIncludeBenchmarks(includeBenchmarks)
  }, [setGroupings, setIncludeBenchmarks])

  const handleColumnsApply = useCallback((columns) => {
    setColumns(columns)
  }, [setColumns])

  const handleGlobalDateChange = useCallback((e) => {
    const value = e.target.value
    if (value === '__custom__') {
      setDateRange(null, null)
      return
    }
    setQuickDate(value)
  }, [setQuickDate, setDateRange])

  const handleStartDateChange = useCallback((e) => {
    setDateRange(e.target.value || null, config.endDate)
  }, [setDateRange, config.endDate])

  const handleEndDateChange = useCallback((e) => {
    setDateRange(config.startDate, e.target.value || null)
  }, [setDateRange, config.startDate])

  const handleBackToPresets = useCallback(() => {
    setQuickDate('YTD')
  }, [setQuickDate])

  const handleFetchAllChange = useCallback((e) => {
    setFetchAllLevels(e.target.checked)
  }, [setFetchAllLevels])

  const groupingSummary = config.groupings.length > 0
    ? config.groupings.join(' > ')
    : null

  const columnSummary = getColumnSummary(config.columns)

  return (
    <div className={classes.toolbarWrapper}>
      <div className={classes.configBar}>
        <div className={classes.clientBadge}>
          <ClientContextBadge />
        </div>
        <div className={classes.viewsSection}>
          <button className={classes.viewsBtn} onClick={handleViewsClick}>
            Manage ▾
          </button>
          <Menu
            anchorEl={viewsMenuAnchor}
            open={Boolean(viewsMenuAnchor)}
            onClose={handleViewsClose}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={handleSaveInPlace} disabled={!activeConfigId || !isDirty}>Save</MenuItem>
            <MenuItem onClick={handleSaveViewDialog}>Save View As...</MenuItem>
            <MenuItem onClick={handleOpenViewDialog}>Open View...</MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleViewsClose()
                if (onOpenPrefs) onOpenPrefs()
              }}
            >
              <span className={classes.menuItemRow}>
                <span>Preferences...</span>
                <span className={classes.kbdHint}>{detectShortcutLabel()}</span>
              </span>
            </MenuItem>
            <RecentViewsSection
              savedConfigs={savedConfigs}
              recentViews={recentViews}
              activeConfigId={activeConfigId}
              onLoadConfig={onLoadConfig}
              onClose={handleViewsClose}
              classes={classes}
            />
          </Menu>
          {activeViewName
            ? <span className={classes.viewName}>{activeViewName}{isDirty ? ' *' : ''}</span>
            : <span className={classes.viewNameMuted}>Unsaved</span>}
        </div>
      </div>
      <div className={classes.toolbar}>
        <div className={classes.field}>
          <label className={classes.label}>Grouping</label>
          <div
            className={classes.summaryBox}
            onClick={() => setGroupingDialogOpen(true)}
          >
            {groupingSummary
              ? <span className={classes.summaryText}>{groupingSummary}</span>
              : <span className={`${classes.summaryText} ${classes.summaryPlaceholder}`}>No grouping selected</span>}
            {config.groupings.length > 0 && (
              <span className={classes.summaryCount}>{config.groupings.length}</span>
            )}
          </div>
        </div>
        <div className={classes.field}>
          <label className={classes.label}>Columns</label>
          <div
            className={classes.summaryBox}
            onClick={() => setColumnDialogOpen(true)}
          >
            {columnSummary
              ? <span className={classes.summaryText}>{columnSummary}</span>
              : <span className={`${classes.summaryText} ${classes.summaryPlaceholder}`}>No columns selected</span>}
            {config.columns.length > 0 && (
              <span className={classes.summaryCount}>{config.columns.length}</span>
            )}
          </div>
        </div>
        <div className={classes.fieldNarrow}>
          <label className={classes.label}>As of</label>
          <input
            type='date'
            className={classes.dateInput}
            value={asOfDate || ''}
            onChange={(e) => setAsOfDate(e.target.value)}
          />
        </div>
        <div className={classes.fieldNarrow}>
          <label className={classes.label}>Date Range</label>
          {isCustomDateRange ? (
            <div className={classes.dateRangeRow}>
              <input
                type='date'
                className={classes.dateInput}
                value={config.startDate || ''}
                onChange={handleStartDateChange}
              />
              <input
                type='date'
                className={classes.dateInput}
                value={config.endDate || ''}
                onChange={handleEndDateChange}
              />
              <IconButton
                className={classes.closeBtn}
                size='small'
                onClick={handleBackToPresets}
                title='Back to presets'
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          ) : (
            <SydSelect
              options={GLOBAL_DATE_OPTIONS}
              value={config.quickDate}
              onChange={handleGlobalDateChange}
              size='xs'
              minWidth='120px'
            />
          )}
        </div>
        <div className={classes.actions}>
          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <Checkbox
                checked={config.fetchAllLevels}
                onChange={handleFetchAllChange}
                size='small'
                color='primary'
              />
            }
            label='Fetch all'
          />
          <SydButton
            icon={ICON_NAMES.queryStats}
            variant='primary'
            size='sm'
            onClick={onRefresh}
            processing={isFetching}
            disabled={!canRun}
          >
            Run
          </SydButton>
        </div>
        <CustomGroupingDialog
          open={groupingDialogOpen}
          onClose={() => setGroupingDialogOpen(false)}
          currentGroupings={config.groupings}
          currentIncludeBenchmarks={config.includeBenchmarks}
          onApply={handleGroupingApply}
        />
        <ColumnBuilderDialog
          open={columnDialogOpen}
          onClose={() => setColumnDialogOpen(false)}
          currentColumns={config.columns}
          onApply={handleColumnsApply}
        />
      </div>
      <SaveViewDialog
        open={saveViewDialogOpen}
        onClose={() => setSaveViewDialogOpen(false)}
        serializeConfig={serializeConfig}
        onSaveComplete={onSaveComplete}
      />
      <OpenViewDialog
        open={openViewDialogOpen}
        onClose={() => setOpenViewDialogOpen(false)}
        savedConfigs={savedConfigs}
        activeConfigId={activeConfigId}
        onLoadConfig={onLoadConfig}
        onDeleteConfig={onDeleteConfig}
      />
    </div>
  )
}

ExplorerConfigToolbar.propTypes = {
  config: PropTypes.shape({
    groupings: PropTypes.arrayOf(PropTypes.string),
    groupingPreset: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.object),
    dateMode: PropTypes.string,
    quickDate: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    fetchAllLevels: PropTypes.bool,
    includeBenchmarks: PropTypes.bool
  }).isRequired,
  setGroupings: PropTypes.func.isRequired,
  setColumns: PropTypes.func.isRequired,
  setQuickDate: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setFetchAllLevels: PropTypes.func.isRequired,
  setIncludeBenchmarks: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  onRefresh: PropTypes.func,
  canRun: PropTypes.bool,
  savedConfigs: PropTypes.arrayOf(PropTypes.object),
  activeConfigId: PropTypes.number,
  activeViewName: PropTypes.string,
  isDirty: PropTypes.bool,
  onLoadConfig: PropTypes.func.isRequired,
  onDeleteConfig: PropTypes.func.isRequired,
  onRefreshConfigs: PropTypes.func,
  serializeConfig: PropTypes.func.isRequired,
  onSaveComplete: PropTypes.func.isRequired,
  recentViews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  })),
  asOfDate: PropTypes.string,
  setAsOfDate: PropTypes.func,
  onOpenPrefs: PropTypes.func
}
